import { useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import currency from "currency.js";
import { DateTime } from "luxon";
import { Constants, helpers, Enums } from "@shared";
import { useSessionContext } from '@contexts';
import { ChevronDownIcon, ChevronUpIcon, PencilIcon, SplitIcon } from "@icons";
import { Tooltip, Badge, Button, IconButton } from "@common";
import type { Invitation, CurrentBill, InvoicePayor, Invoice } from '@app-types';

interface ActivePayorsProps {
    invitations: Invitation[];
    currentPayorId: number;
    isGuarantor: boolean;
    currentBill: CurrentBill;
    totalAccountPayors?: number;
}

const ActivePayors = ({ invitations, currentPayorId, isGuarantor, currentBill, totalAccountPayors }: ActivePayorsProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { session } = useSessionContext();

    // Sort invitations to put current user first
    const sortedInvitations = [...invitations].sort((a, b) => {
        if (a.idPayor === currentPayorId) return -1;
        if (b.idPayor === currentPayorId) return 1;
        return 0;
    });

    const formatAutodrawDate = (autodrawDate: string | null) => {
        if (!autodrawDate) return null;
        const date = DateTime.fromISO(autodrawDate);
        return date.toLocaleString({ year: `2-digit`, month: `2-digit`, day: `2-digit` });
    };

    const prepareInvoicePayors = () => {
        const { invitations, ...rest } = currentBill;
        const { idProductType } = rest;
        const invoice: Omit<Invoice, `invitations`> = rest;

        const invoicePayors: InvoicePayor[] = invitations.map((i) => {
            const { idPayor, idInvitation, paid, amount: payAmount, payPercentage, autodrawDate, fullName, email, isGuarantor } = i;
            const dueDay = autodrawDate ? DateTime.fromISO(autodrawDate).day : null;
            return {
                idPayor,
                idInvitation,
                fullName,
                email,
                payAmount,
                payPercentage,
                dueDay,
                amountType: Enums.AmountType.Percentage,
                alreadySelected: true,
                paid,
                isGuarantor
            };
        });

        return { invoicePayors, idProductType, invoice };
    };

    const handleEdit = (idPayor: number) => {
        const { invoicePayors, idProductType, invoice } = prepareInvoicePayors();

        helpers.navigateStart(location, navigate, Constants.Flows.updateEnrolledInvoice.id, undefined, {
            account: { ...session },
            idProductType,
            invoicePayors,
            invoice,
            idEditPayor: idPayor
        });
    };

    const handleSplit = () => {
        const { invoicePayors, idProductType, invoice } = prepareInvoicePayors();

        helpers.navigateStart(location, navigate, Constants.Flows.updateEnrolledInvoice.id, undefined, {
            account: { ...session },
            idProductType,
            invoicePayors,
            invoice
        });
    };

    const handleAddPayor = () => {
        navigate(`/add-payor`);
    };

    return (
        <div className={`bg-white border rounded-[12px] p-4 ${isOpen ? `border-neutral-300` : `border-neutral-100 hover:border-neutral-200`}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full focus-visible:outline-focus-ring"
                aria-expanded={isOpen}
                aria-label="Toggle active payors">
                <h2 className="font-medium text-base leading-6 text-neutral-950 tracking-[0.16px]">
                    Active Payors
                </h2>
                {isOpen ? (
                    <ChevronUpIcon className="h-4 w-4 text-neutral-950" />
                ) : (
                    <ChevronDownIcon className="h-4 w-4 text-neutral-950" />
                )}
            </button>

            {isOpen && (
                <div className="flex flex-col gap-4 mt-4">
                    {sortedInvitations.map((invitation) => {
                        const isCurrentUser = invitation.idPayor === currentPayorId;
                        const autodrawDate = formatAutodrawDate(invitation.autodrawDate);

                        return (
                            <div
                                key={invitation.idPayor}
                                className="border border-neutral-100 rounded-[12px] px-6 py-4 flex items-center justify-between h-[80px]">
                                <div className="flex flex-col gap-1 justify-center min-w-0 flex-1 mr-3">
                                    <div className="flex items-start gap-2 min-w-0">
                                        <span className="text-base leading-6 text-neutral-950 tracking-[0.16px] truncate">
                                            {invitation.fullName}
                                        </span>
                                        {isCurrentUser && (
                                            <Badge className="flex-shrink-0">You</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-base leading-6 text-neutral-950 tracking-[0.16px]">
                                            {currency(invitation.balance).format()}
                                        </span>
                                        {autodrawDate && (
                                            <span className="text-sm leading-5 text-neutral-950 tracking-[0.14px]">
                                                Autopay on {autodrawDate}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {!invitation.isGuarantor && (
                                    <Tooltip
                                        content="Edit allocation"
                                        position="top"
                                    >
                                        <IconButton
                                            variant="tertiary"
                                            size="lg"
                                            onClick={() => handleEdit(invitation.idPayor)}
                                            aria-label={`Edit ${invitation.fullName}`}>
                                            <PencilIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}

                    {isGuarantor && currentBill && (
                        <div className="flex flex-row gap-4">
                            {(totalAccountPayors === undefined || totalAccountPayors > 1) && (
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    startIcon={<SplitIcon />}
                                    onClick={handleSplit}
                                >
                                    Split
                                </Button>
                            )}
                            <Button
                                variant="secondary"
                                size="lg"
                                className="flex-1"
                                onClick={handleAddPayor}
                                aria-label="Add a New Payor"
                            >
                                <span className="sm:hidden">Add New Payor</span>
                                <span className="hidden sm:inline">Add a New Payor</span>
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ActivePayors;
